import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "results", "collaboratorId", "newFields"]
  static values = { searchUrl: String }

  connect() {
    this._debounce = null
    this._activeIndex = -1
    document.addEventListener("click", this._onOutsideClick = (e) => {
      if (!this.element.contains(e.target)) this.hide()
    })
    // When the surrounding form is reset (e.g. after a successful submission
    // via the global data-reset-on-submit handler), also clear our internal
    // UI state — the dropdown, hidden collaborator id, and "new fields" panel.
    this._form = this.element.closest("form")
    if (this._form) {
      this._form.addEventListener("reset", this._onFormReset = () => {
        this._activeIndex = -1
        this.hide()
        this.showNewFields()
      })
    }
  }

  disconnect() {
    document.removeEventListener("click", this._onOutsideClick)
    if (this._form && this._onFormReset) {
      this._form.removeEventListener("reset", this._onFormReset)
    }
  }

  search() {
    clearTimeout(this._debounce)
    const q = this.inputTarget.value
    this.collaboratorIdTarget.value = ""
    this._activeIndex = -1
    if (q.trim().length < 2) {
      this.hide()
      this.showNewFields()
      return
    }
    this._debounce = setTimeout(() => this.performSearch(q), 200)
  }

  async performSearch(q) {
    try {
      const url = `${this.searchUrlValue}?q=${encodeURIComponent(q)}`
      const response = await fetch(url, { headers: { "Accept": "application/json" } })
      if (!response.ok) return this.hide()
      const data = await response.json()
      this.render(data)
    } catch (e) {
      this.hide()
    }
  }

  render(items) {
    this.resultsTarget.innerHTML = ""
    if (!items || items.length === 0) {
      this.hide()
      return
    }
    items.forEach((item, index) => {
      const row = document.createElement("div")
      row.className = "px-3 py-2 hover:bg-gray-800 cursor-pointer flex items-center gap-2 text-sm text-white"
      row.dataset.index = index
      row.dataset.id = item.id
      row.dataset.name = item.name
      row.dataset.email = item.email || ""
      row.dataset.instagram = item.instagram_handle || ""

      const avatar = item.logo_url
        ? `<img src="${item.logo_url}" class="w-7 h-7 rounded-full object-cover" />`
        : `<div class="w-7 h-7 rounded-full bg-gray-700 flex items-center justify-center text-gray-300 text-xs font-bold">${(item.name || "?").charAt(0).toUpperCase()}</div>`

      const meta = []
      if (item.email) meta.push(item.email)
      if (item.instagram_handle) meta.push("@" + item.instagram_handle)
      const metaHtml = meta.length ? `<span class="text-xs text-gray-400 ml-2">${meta.join(" · ")}</span>` : ""
      const claimed = item.claimed ? `<span class="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-green-700 text-green-100">Member</span>` : ""

      row.innerHTML = `${avatar}<span class="font-medium">${item.name}</span>${metaHtml}${claimed}`
      row.addEventListener("click", () => this.select(item))
      this.resultsTarget.appendChild(row)
    })
    this.resultsTarget.classList.remove("hidden")
  }

  select(item) {
    this.inputTarget.value = item.name
    this.collaboratorIdTarget.value = item.id
    this.hide()
    this.hideNewFields()
  }

  navigate(event) {
    if (this.resultsTarget.classList.contains("hidden")) return
    const rows = Array.from(this.resultsTarget.children)
    if (event.key === "ArrowDown") {
      event.preventDefault()
      this._activeIndex = Math.min(this._activeIndex + 1, rows.length - 1)
      this.highlight(rows)
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      this._activeIndex = Math.max(this._activeIndex - 1, 0)
      this.highlight(rows)
    } else if (event.key === "Enter" && this._activeIndex >= 0) {
      event.preventDefault()
      const row = rows[this._activeIndex]
      if (row) {
        this.select({
          id: row.dataset.id,
          name: row.dataset.name,
          email: row.dataset.email,
          instagram_handle: row.dataset.instagram
        })
      }
    } else if (event.key === "Escape") {
      this.hide()
    }
  }

  highlight(rows) {
    rows.forEach((r, i) => {
      if (i === this._activeIndex) {
        r.classList.add("bg-gray-800")
      } else {
        r.classList.remove("bg-gray-800")
      }
    })
  }

  hide() {
    this.resultsTarget.classList.add("hidden")
    this.resultsTarget.innerHTML = ""
  }

  showNewFields() {
    if (this.hasNewFieldsTarget) this.newFieldsTarget.classList.remove("hidden")
  }

  hideNewFields() {
    if (this.hasNewFieldsTarget) this.newFieldsTarget.classList.add("hidden")
  }
}
